import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';

// ─────────────────────────────────────────────────────────────────────────
// ModernProcess — the «Moderno» theme "Our Process" (s139, approved by Anthony:
// P4 "Light Rail"). Stone band: header left (eyebrow/title/subtitle/CTA), a
// vertical rail on the right with a numbered node per step (step_number, or the
// position); the last step is the outcome node in graphite.
// Data = aurex_process (config + items step_number/title/description).
// Colours come only from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";

export default function ModernProcess({ config = {}, items = [] }) {
  const tt = useT();
  const { lang } = useLang();
  const steps = (items || []).filter(i => i.visible !== false && itemHasLocale(i.title, lang));
  if (!steps.length) return null;

  const title = tt(config.title);
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const last = words.length > 1 ? words.pop() : null;
  const ctaText = tt(config.cta_text);

  return (
    <section id="aurex-process" data-testid="aurex-section-process" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28 grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-12 lg:gap-20 items-start">
        <div className="flex flex-col gap-5 lg:sticky lg:top-28">
          {tt(config.eyebrow) && (
            <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
              <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(config.eyebrow)}
            </div>
          )}
          {title && (
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="process-title">
              {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
            </h2>
          )}
          {tt(config.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)', maxWidth: '30rem' }}>{tt(config.subtitle)}</p>}
          {ctaText && config.cta_url && (
            <a href={config.cta_url} target={config.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" data-testid="process-cta"
              className="inline-flex items-center gap-2 self-start font-semibold text-sm px-6 py-3.5 rounded-[2px] mt-2 mx-btn mx-btn--primary"
              style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' }}>
              {ctaText} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        <ol className="relative m-0 p-0 list-none flex flex-col gap-10 pl-16 sm:pl-20">
          <span aria-hidden="true" className="absolute top-2 bottom-2 w-[2px]" style={{ left: 23, background: 'rgba(20,26,58,.12)' }} />
          {steps.map((s, idx) => {
            const isLast = idx === steps.length - 1;
            const n = s.step_number || idx + 1;
            return (
              <li key={s.id} className="relative" data-testid={`process-step-${s.id}`}>
                <span className="absolute grid place-items-center w-12 h-12 rounded-full -left-16 sm:-left-20 -top-1 text-lg font-semibold"
                  style={{ fontFamily: SERIF, fontVariantNumeric: 'tabular-nums', background: isLast ? '#16171a' : '#fff', border: `2px solid ${isLast ? '#16171a' : 'var(--color-accent, #ff9c1b)'}`, color: isLast ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }}>
                  {n}
                </span>
                <h3 className="font-bold" style={{ fontFamily: SERIF, fontSize: '1.45rem', lineHeight: 1.2, color: 'var(--color-heading, #1a2332)' }}>{tt(s.title)}</h3>
                {tt(s.description) && <div className="rich-text-content mt-2 text-base leading-relaxed" style={{ color: 'var(--color-body-text, #475569)', maxWidth: '38rem' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(tt(s.description)) }} />}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
